/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Building2,
  User,
  Shield,
  Lock,
  Activity,
  Info,
} from 'lucide-react';
import { userService } from '../services/userService';
import StatusBadge from '../components/StatusBadge';
import toastService from '../utils/toastService';
import isEmail from 'validator/lib/isEmail';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { FIELD_LIMITS, validateUsername } from '../utils/constants';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL } from '../constants/entityLabels';

const USERS_ROUTE = '/users';
const SCOPE_PLATFORM = 'platform';
const SCOPE_ACCOUNT = 'account';
const SCOPE_SUB_ACCOUNT = 'sub_account';
const STATUS_ACTIVE = 'active';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])\S{8,128}$/;

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  // Username is set-once: when the user already has one it is shown read-only
  const [originalUsername, setOriginalUsername] = useState('');

  // Form states
  const [form, setForm] = useState({
    email: '',
    username: '',
    externalUserId: '',
    firstName: '',
    lastName: '',
    displayName: '',
    password: '',
    roleKeys: [],
    status: STATUS_ACTIVE,
    accountUID: '',
    subAccountUID: '',
  });

  const currentScope = currentUser?.scope || SCOPE_PLATFORM;
  const isPlatformAdmin = currentUser?.scope === SCOPE_PLATFORM;
  const isAccountAdmin = currentUser?.scope === SCOPE_ACCOUNT;

  // Filter roles based on current context
  const filteredRoles = useMemo(() => {
    // Filter out platform_admin role unless the current user is a platform_admin
    const isPlatformAdminRole = currentUser?.role === 'platform_admin';

    let filtered = roles;

    // Hide platform_admin role from everyone except platform_admin users
    if (!isPlatformAdminRole) {
      filtered = filtered.filter(r => r.id !== 'PLATFORM_ADMIN' && r.key !== 'Platform Admin');
    }

    // Platform admin with no context: show all roles
    if (isPlatformAdmin && !currentUser?.accountUID && !currentUser?.subAccountUID) {
      return filtered;
    }

    // Platform admin in account context: show account and sub-account roles
    if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
      return filtered.filter(r => r.rawScope === SCOPE_ACCOUNT || r.rawScope === SCOPE_SUB_ACCOUNT);
    }

    // Platform admin in sub-account context: show only sub-account roles
    if (isPlatformAdmin && currentUser?.subAccountUID) {
      return filtered.filter(r => r.rawScope === SCOPE_SUB_ACCOUNT);
    }

    // Account admin: show account and sub-account roles
    if (isAccountAdmin) {
      return filtered.filter(r => r.rawScope === SCOPE_ACCOUNT || r.rawScope === SCOPE_SUB_ACCOUNT);
    }

    // Sub-account admin: show only sub-account roles
    if (currentScope === SCOPE_SUB_ACCOUNT) {
      return filtered.filter(r => r.rawScope === SCOPE_SUB_ACCOUNT);
    }

    return filtered;
  }, [roles, currentScope, isPlatformAdmin, isAccountAdmin, currentUser?.role, currentUser?.accountUID, currentUser?.subAccountUID]);

  // Derive the scope required by selected roles
  const requiredScope = useMemo(() => {
    if (!form.roleKeys.length) { return null; }
    const selected = filteredRoles.filter(r => form.roleKeys.includes(r.id));
    if (selected.some(r => r.rawScope === SCOPE_SUB_ACCOUNT)) { return SCOPE_SUB_ACCOUNT; }
    if (selected.some(r => r.rawScope === SCOPE_ACCOUNT)) { return SCOPE_ACCOUNT; }
    return SCOPE_PLATFORM;
  }, [form.roleKeys, filteredRoles]);

  // Track previous scope to detect changes
  const [prevScope, setPrevScope] = useState(null);

  // Determine if account dropdown should be shown
  const needsAccount = isPlatformAdmin && !currentUser?.accountUID && (requiredScope === SCOPE_ACCOUNT || requiredScope === SCOPE_SUB_ACCOUNT);

  const needsSubAccount = (() => {
    // Platform admin in platform context: show dropdown for sub-account roles
    if (isPlatformAdmin && !currentUser?.accountUID && !currentUser?.subAccountUID) {
      return requiredScope === SCOPE_SUB_ACCOUNT;
    }
    // Platform admin with account context: show dropdown for sub-account roles
    if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
      return requiredScope === SCOPE_SUB_ACCOUNT;
    }
    // Platform admin with sub-account context: never show sub-account dropdown
    if (isPlatformAdmin && currentUser?.subAccountUID) {
      return false;
    }
    // Account admin: show dropdown for sub-account roles
    if (isAccountAdmin) {
      return requiredScope === SCOPE_SUB_ACCOUNT;
    }
    return false;
  })();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [userRes, rolesRes] = await Promise.all([
          userService.getUserById(id),
          userService.getRoles()
        ]);

        const scopeLabelMap = {
          account: ACCOUNT_LABEL.singular,
          platform: 'Platform',
          sub_account: SUB_ACCOUNT_LABEL.singular,
        };

        // Map roles for single select
        const rolesData = rolesRes.data?.roles || rolesRes.data?.data || (Array.isArray(rolesRes.data) ? rolesRes.data : []);
        const mappedRoles = rolesData.map(r => ({
          id: r.key || (r.name ? r.name.toUpperCase() : r.id),
          key: r.name,
          displayName: r.displayName || r.name,
          module: r.scope === SCOPE_SUB_ACCOUNT ? SUB_ACCOUNT_LABEL.singular : scopeLabelMap[r.scope],
          rawScope: r.scope,
          description: r.description
        }));
        setRoles(mappedRoles);

        const userData = userRes.data;
        const profile = userData.profiles?.[0] || {};
        setOriginalUsername(userData.username || '');
        setForm({
          email: userData.email || '',
          username: userData.username || '',
          externalUserId: userData.externalUserId || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          displayName: userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          password: '',
          roleKeys: (profile.roles || userData.roles || []).map(r => r.key || (r.name ? r.name.toUpperCase() : '')),
          status: userData.status || STATUS_ACTIVE,
          accountUID: profile.accountUID || '',
          subAccountUID: profile.subAccountUID || '',
        });
      } catch {
        toastService.error('Failed to load user data');
        navigate(USERS_ROUTE);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, navigate]);

  // Load accounts for platform admin when account picker appears
  useEffect(() => {
    if (!needsAccount || accounts.length) { return; }
    apiClient.get('/accounts', { params: { limit: 100, page: 1 } })
      .then(({ data }) => { setAccounts(data.data || []); })
      .catch(() => toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`));
  }, [needsAccount, accounts.length]);

  // Load sub-accounts when account is selected or from context
  useEffect(() => {
    if (!needsSubAccount) { setSubAccounts([]); return; }

    let accountId;
    if (isPlatformAdmin && !currentUser?.accountUID) {
      // Platform admin in platform context: use selected account
      accountId = form.accountUID;
    } else if (isPlatformAdmin && currentUser?.accountUID) {
      // Platform admin with account context: use context account
      accountId = currentUser.accountUID;
    } else if (isAccountAdmin) {
      // Account admin: use their account
      accountId = currentUser?.accountUID;
    } else {
      // Sub-account admin: use their account
    }

    if (!accountId) { setSubAccounts([]); return; }
    apiClient.get(`/accounts/${accountId}/sub-accounts`, { params: { limit: 100, page: 1 } })
      .then(({ data }) => setSubAccounts(data.subAccounts || data.data || []))
      .catch(() => toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.pluralLower}`));
  }, [needsSubAccount, form.accountUID, currentUser?.accountUID, isPlatformAdmin, isAccountAdmin]);

  // Reset account/sub-account only when scope changes
  useEffect(() => {
    if (prevScope !== null && prevScope !== requiredScope) {
      setForm(f => ({ ...f, accountUID: '', subAccountUID: '' }));
      setSubAccounts([]);
    }
    setPrevScope(requiredScope);
  }, [requiredScope, prevScope]);

  const validatePassword = (password) => {
    if (!password?.trim()) {
      return null; // Optional for Edit User
    }

    return PASSWORD_REGEX.test(password)
      ? null
      : 'Password must be between 8 and 128 characters long and include at least one uppercase letter, one lowercase letter, one number, one special character, and no spaces.';
  };

  const validateForm = () => {
    const errors = {};
    if (!form.email) { errors.email = 'Email is required'; }
    else if (!isEmail(form.email)) { errors.email = 'Invalid email format'; }

    if (!originalUsername) {
      const usernameError = validateUsername(form.username);
      if (usernameError) { errors.username = usernameError; }
    }

    if (!form.firstName.trim()) { errors.firstName = 'First name is required'; }
    if (!form.lastName.trim()) { errors.lastName = 'Last name is required'; }
    if (!form.displayName.trim()) { errors.displayName = 'Display name is required'; }

    if (!form.roleKeys || form.roleKeys.length === 0) {
      errors.roleKeys = 'At least one role is required';
    }

    // Only validate account selection if dropdown is shown
    if (needsAccount && !form.accountUID && !currentUser?.accountUID) {
      errors.accountUID = `${ACCOUNT_LABEL.singular} is required for this role`;
    }

    // Only validate sub-account selection if dropdown is shown
    if (needsSubAccount && !form.subAccountUID && !currentUser?.subAccountUID) {
      errors.subAccountUID = `${SUB_ACCOUNT_LABEL.singular} is required for this role`;
    }

    const passwordError = validatePassword(form.password);
    if (passwordError) { errors.password = passwordError; }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (['firstName', 'lastName', 'displayName', 'username'].includes(field) && typeof value === 'string' && value !== value.trim()) {
      value = value.trim();
      setForm(f => ({ ...f, [field]: value }));
    }
    if (field === 'username') {
      // Optional field — only validate the format when a value is entered
      setFieldErrors(prev => ({ ...prev, username: validateUsername(value) }));
      return;
    }
    if (!value || (typeof value === 'string' && !value.trim())) {
      if (field === 'password') { return; } // Password is optional on edit
      const fieldLabels = {
        firstName: 'First name',
        lastName: 'Last name',
        displayName: 'Display name',
        email: 'Email',
        roleKeys: 'Role',
        accountUID: ACCOUNT_LABEL.singular,
        subAccountUID: SUB_ACCOUNT_LABEL.singular
      };
      setFieldErrors(prev => ({ ...prev, [field]: `${fieldLabels[field] || field} is required` }));
    } else if (field === 'email' && !isEmail(value)) {
      setFieldErrors(prev => ({ ...prev, email: 'Invalid email format' }));
    } else if (field === 'password') {
      const error = validatePassword(value);
      if (error) setFieldErrors(prev => ({ ...prev, password: error }));
    } else {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }

    try {
      setSubmitting(true);
      const payload = {
        email: form.email,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        displayName: form.displayName.trim(),
        roleKeys: form.roleKeys,
        status: form.status || STATUS_ACTIVE,
        // Empty string clears the external ID
        externalUserId: form.externalUserId.trim(),
      };

      // Username can only be set once — send it only when the user had none
      const username = form.username.trim();
      if (!originalUsername && username) {
        payload.username = username;
      }

      // Only include password if it's provided
      if (form.password && form.password.trim() !== '') {
        payload.password = form.password;
      }

      // Add account/sub-account based on context and role scope
      if (requiredScope === SCOPE_PLATFORM) {
        // Platform role: set both to null
        payload.accountUID = null;
        payload.subAccountUID = null;
      } else if (requiredScope === SCOPE_ACCOUNT) {
        // Account role: set accountUID, subAccountUID to null
        if (isPlatformAdmin && !currentUser?.accountUID) {
          // Platform admin in platform context: use selected account
          payload.accountUID = form.accountUID || null;
        } else if (isPlatformAdmin && currentUser?.accountUID) {
          // Platform admin with account context: use context account
          payload.accountUID = currentUser.accountUID;
        } else if (isAccountAdmin) {
          // Account admin: use their account
          payload.accountUID = currentUser?.accountUID || null;
        } else if (currentScope === SCOPE_SUB_ACCOUNT) {
          // Sub-account admin: use their account
          payload.accountUID = currentUser?.accountUID || null;
        } else {
          payload.accountUID = null;
        }
        payload.subAccountUID = null;
      } else if (requiredScope === SCOPE_SUB_ACCOUNT) {
        // Sub-account role: set both
        if (isPlatformAdmin && !currentUser?.accountUID) {
          // Platform admin in platform context: use selected values
          payload.accountUID = form.accountUID || null;
          payload.subAccountUID = form.subAccountUID || null;
        } else if (isPlatformAdmin && currentUser?.accountUID && !currentUser?.subAccountUID) {
          // Platform admin with account context: use context account, selected sub-account
          payload.accountUID = currentUser.accountUID;
          payload.subAccountUID = form.subAccountUID || null;
        } else if (isPlatformAdmin && currentUser?.subAccountUID) {
          // Platform admin with sub-account context: use both from context
          payload.accountUID = currentUser.accountUID;
          payload.subAccountUID = currentUser.subAccountUID;
        } else if (isAccountAdmin) {
          // Account admin: use their account, selected sub-account
          payload.accountUID = currentUser?.accountUID || null;
          payload.subAccountUID = form.subAccountUID || null;
        } else if (currentScope === SCOPE_SUB_ACCOUNT && currentUser?.subAccountUID) {
          // Sub-account admin: use their account and sub-account
          payload.accountUID = currentUser?.accountUID || null;
          payload.subAccountUID = currentUser.subAccountUID;
        } else {
          payload.accountUID = null;
          payload.subAccountUID = null;
        }
      } else {
        payload.accountUID = null;
        payload.subAccountUID = null;
      }

      await userService.updateUser(id, payload);
      toastService.success('User updated successfully');
      navigate(USERS_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || 'Failed to update user');
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  const selectClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:bg-base-200/50 disabled:text-base-content/40 ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse p-2 w-full">
        <div className="h-6 w-48 bg-base-300 rounded-lg"></div>
        <div className="glass-card rounded-2xl p-6 h-36 bg-base-100/60"></div>
        <div className="glass-card rounded-2xl p-6 h-48 bg-base-100/60"></div>
        <div className="glass-card rounded-2xl p-6 h-48 bg-base-100/60"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(USERS_ROUTE)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to Users
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                User Account
              </span>
              <StatusBadge status={form.status} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              {form.displayName || `${form.firstName} ${form.lastName}`.trim() || 'Update User'}
            </h1>
            <p className="text-xs text-base-content/50">
              Modify security role, organization access, and user credentials.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Role, Organization & Scope */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Building2 size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Organization & Scope
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Role Select (Always First) */}
            <div className={`space-y-1.5 ${(!needsAccount && !needsSubAccount) ? 'sm:col-span-2' : ''}`}>
              <label htmlFor="role" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Shield size={12} className="text-primary" />
                <span>Role *</span>
              </label>
              <select
                id="role"
                className={selectClass('roleKeys')}
                value={form.roleKeys[0] || ''}
                onBlur={e => handleBlur('roleKeys', e.target.value)}
                onChange={e => {
                  setForm(f => ({ ...f, roleKeys: e.target.value ? [e.target.value] : [] }));
                  if (fieldErrors.roleKeys) setFieldErrors(prev => ({ ...prev, roleKeys: null }));
                }}
              >
                <option value="">— Select a role —</option>
                {filteredRoles.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.displayName} ({role.module})
                  </option>
                ))}
              </select>
              {fieldErrors.roleKeys && <p className="text-error text-xs font-medium">{fieldErrors.roleKeys}</p>}
            </div>

            {/* Account Picker (Shown when role requires account scope) */}
            {needsAccount && (
              <div className="space-y-1.5">
                <label htmlFor="account" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                  {ACCOUNT_LABEL.singular} *
                </label>
                <select
                  id="account"
                  className={selectClass('accountUID')}
                  value={form.accountUID}
                  onBlur={e => handleBlur('accountUID', e.target.value)}
                  onChange={e => {
                    setForm(f => ({ ...f, accountUID: e.target.value, subAccountUID: '' }));
                    if (fieldErrors.accountUID) setFieldErrors(prev => ({ ...prev, accountUID: null }));
                  }}
                >
                  <option value="">{`— Select ${ACCOUNT_LABEL.withArticle} —`}</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {fieldErrors.accountUID && <p className="text-error text-xs font-medium">{fieldErrors.accountUID}</p>}
              </div>
            )}

            {/* Sub-Account Picker (Shown when role requires sub-account scope) */}
            {needsSubAccount && (
              <div className="space-y-1.5">
                <label htmlFor="subAccount" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                  {SUB_ACCOUNT_LABEL.singular} *
                </label>
                <select
                  id="subAccount"
                  className={selectClass('subAccountUID')}
                  value={form.subAccountUID}
                  onChange={e => {
                    setForm(f => ({ ...f, subAccountUID: e.target.value }));
                    if (fieldErrors.subAccountUID) setFieldErrors(prev => ({ ...prev, subAccountUID: null }));
                  }}
                  onBlur={e => handleBlur('subAccountUID', e.target.value)}
                  disabled={isPlatformAdmin && !currentUser?.accountUID && !form.accountUID}
                >
                  <option value="">{isPlatformAdmin && !currentUser?.accountUID && !form.accountUID ? `— Select ${ACCOUNT_LABEL.withArticle} first —` : `— Select ${SUB_ACCOUNT_LABEL.withArticle} —`}</option>
                  {subAccounts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {fieldErrors.subAccountUID && <p className="text-error text-xs font-medium">{fieldErrors.subAccountUID}</p>}
              </div>
            )}
          </div>
        </div>

        {/* User Information */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <User size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              User Information
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-1.5">
              <label htmlFor="firstName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                First Name *
              </label>
              <input
                id="firstName"
                type="text"
                maxLength={30}
                placeholder="e.g. John"
                className={inputClass('firstName')}
                value={form.firstName}
                onBlur={e => handleBlur('firstName', e.target.value)}
                onChange={e => {
                  setForm(f => ({ ...f, firstName: e.target.value }));
                  if (fieldErrors.firstName) setFieldErrors(prev => ({ ...prev, firstName: null }));
                }}
              />
              {fieldErrors.firstName && <p className="text-error text-xs font-medium">{fieldErrors.firstName}</p>}
            </div>

            {/* Last Name */}
            <div className="space-y-1.5">
              <label htmlFor="lastName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                Last Name *
              </label>
              <input
                id="lastName"
                type="text"
                maxLength={30}
                placeholder="e.g. Doe"
                className={inputClass('lastName')}
                value={form.lastName}
                onBlur={e => handleBlur('lastName', e.target.value)}
                onChange={e => {
                  setForm(f => ({ ...f, lastName: e.target.value }));
                  if (fieldErrors.lastName) setFieldErrors(prev => ({ ...prev, lastName: null }));
                }}
              />
              {fieldErrors.lastName && <p className="text-error text-xs font-medium">{fieldErrors.lastName}</p>}
            </div>

            {/* Email Address */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                Email Address *
              </label>
              <input
                id="email"
                type="email"
                maxLength={100}
                placeholder="john.doe@company.com"
                className={inputClass('email')}
                value={form.email}
                onBlur={e => handleBlur('email', e.target.value)}
                onChange={e => {
                  setForm(f => ({ ...f, email: e.target.value }));
                  if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: null }));
                }}
              />
              {fieldErrors.email && <p className="text-error text-xs font-medium">{fieldErrors.email}</p>}
            </div>

            {/* Display Name */}
            <div className="space-y-1.5">
              <label htmlFor="displayName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                Display Name *
              </label>
              <input
                id="displayName"
                type="text"
                maxLength={30}
                placeholder="e.g. John D."
                className={inputClass('displayName')}
                value={form.displayName}
                onBlur={e => handleBlur('displayName', e.target.value)}
                onChange={e => {
                  setForm(f => ({ ...f, displayName: e.target.value }));
                  if (fieldErrors.displayName) setFieldErrors(prev => ({ ...prev, displayName: null }));
                }}
              />
              {fieldErrors.displayName && <p className="text-error text-xs font-medium">{fieldErrors.displayName}</p>}
            </div>

            {/* Username */}
            <div className="space-y-1.5">
              <label htmlFor="username" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                Username
              </label>
              <input
                id="username"
                type="text"
                autoComplete="off"
                maxLength={FIELD_LIMITS.USERNAME_MAX_LENGTH}
                placeholder="Optional, e.g. john.doe"
                className={`${inputClass('username')} ${originalUsername ? 'opacity-60 cursor-not-allowed' : ''}`}
                value={form.username}
                disabled={!!originalUsername}
                readOnly={!!originalUsername}
                onBlur={e => { if (!originalUsername) handleBlur('username', e.target.value); }}
                onChange={e => {
                  setForm(f => ({ ...f, username: e.target.value }));
                  if (fieldErrors.username) setFieldErrors(prev => ({ ...prev, username: null }));
                }}
              />
              {fieldErrors.username
                ? <p className="text-error text-xs font-medium">{fieldErrors.username}</p>
                : <p className="text-base-content/40 text-xs">
                    {originalUsername ? 'Username cannot be changed once set.' : 'Can be used to sign in. Cannot be changed once set.'}
                  </p>}
            </div>

            {/* External User ID */}
            <div className="space-y-1.5">
              <label htmlFor="externalUserId" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                External User ID
              </label>
              <input
                id="externalUserId"
                type="text"
                maxLength={FIELD_LIMITS.EXTERNAL_ID}
                placeholder="Optional"
                className={inputClass('externalUserId')}
                value={form.externalUserId}
                onChange={e => setForm(f => ({ ...f, externalUserId: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Security & Access */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Lock size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              Security & Status
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <label htmlFor="password" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                  <Lock size={12} className="text-primary" />
                  <span>Password</span>
                </label>
                <div
                  className="tooltip tooltip-right before:text-xs before:font-semibold before:whitespace-nowrap"
                  data-tip="Leave blank to keep existing password"
                >
                  <Info size={11} className="text-base-content/40 cursor-help" />
                </div>
              </div>
              <input
                id="password"
                type="password"
                maxLength={128}
                autoComplete="new-password"
                name="new-password"
                placeholder="Leave blank to keep current password"
                className={inputClass('password')}
                value={form.password}
                onBlur={e => handleBlur('password', e.target.value)}
                onChange={e => {
                  setForm(f => ({ ...f, password: e.target.value }));
                  if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: null }));
                }}
              />
              {fieldErrors.password && <p className="text-error text-xs font-medium">{fieldErrors.password}</p>}
            </div>

            {/* Status */}
            <div className="space-y-1.5">
              <label htmlFor="userStatus" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Activity size={12} className="text-primary" />
                <span>Status</span>
              </label>
              <div className="flex items-center gap-4 px-4 py-2.5 bg-base-100 border border-base-300 rounded-xl h-[42px]">
                <label aria-label="User Status Toggle" className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="userStatus"
                    className="sr-only peer"
                    checked={form.status === STATUS_ACTIVE}
                    onChange={e => setForm(f => ({ ...f, status: e.target.checked ? STATUS_ACTIVE : 'inactive' }))}
                  />
                  <div className="w-10 h-5 bg-base-content/10 rounded-full peer peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                </label>
                <label htmlFor="userStatus" className="text-xs font-bold text-base-content/70 cursor-pointer whitespace-nowrap">
                  {form.status === STATUS_ACTIVE ? 'Active' : 'Inactive'}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(USERS_ROUTE)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Saving Changes...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditUser;
